import { Folder } from "@untitledui/icons";
import type { Container } from "@/types/container";
import type { CustomFolder } from "@/types/documents";
import { cx } from "@/utils/cx";
import { useTranslation } from "react-i18next";
import { documentService } from "@/data/mock-documents";

interface FolderCardProps {
    type: "container" | "custom_folder";
    container?: Container;
    customFolder?: CustomFolder;
    onClick?: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
    isSelected?: boolean;
    viewMode?: "card" | "list";
    className?: string;
}

export const FolderCard = ({
    type,
    container,
    customFolder,
    onClick,
    onContextMenu,
    isSelected = false,
    viewMode = "card",
    className,
}: FolderCardProps) => {
    const { t } = useTranslation("pages:documents");

    const name = type === "container" ? container?.containerNumber : customFolder?.name;
    const documentCount = type === "container"
        ? documentService.getByContainerId(container?.id || "").length
        : customFolder?.containerIds.reduce((acc, containerId) => {
            return acc + documentService.getByContainerId(containerId).length;
        }, 0) || 0;

    // Format document count with translation
    const documentsCountText = t("drive.folder.documentsCount", { count: documentCount });

    if (viewMode === "list") {
        return (
            <div
                className={cx(
                    "flex items-center gap-3 rounded-lg px-4 py-3 cursor-pointer transition-all",
                    "hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2",
                    isSelected && "bg-active ring-2 ring-brand",
                    className
                )}
                onClick={onClick}
                onContextMenu={onContextMenu}
                role="button"
                tabIndex={0}
            >
                <Folder className="size-6 text-brand-600" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">{name}</p>
                    <p className="text-xs text-tertiary">
                        {type === "container" ? t("drive.folder.container") : t("drive.folder.customFolder")}
                    </p>
                </div>
                <div className="text-xs text-tertiary">
                    {documentsCountText}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cx(
                "rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all",
                "hover:ring-2 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2",
                isSelected && "ring-2 ring-brand shadow-md",
                className
            )}
            onClick={onClick}
            onContextMenu={onContextMenu}
            role="button"
            tabIndex={0}
        >
            <div className="flex flex-col items-center text-center gap-3">
                <Folder className="size-8 text-brand-600" />
                <div className="w-full min-w-0">
                    <p className="text-sm font-medium text-primary truncate" title={name}>
                        {name}
                    </p>
                    <p className="text-xs text-tertiary mt-1">
                        {documentsCountText}
                    </p>
                </div>
            </div>
        </div>
    );
};

